import { isNil } from '~/utilities/type-guards';
import type { NotificationStoreActionsType } from '~/components/NotificationTray/NotificationStore';
import { v4 } from 'uuid';
import type { FileState, QueueState, UploaderFunctions } from './types';

export const initialState = [];

const secondsInMinute = 60;
const secondsInHour = 3600;
const msInSec = 1000;
const hoursInDay = 24;
const million = 1000000;
const byte = 8;

export interface ActionAddFiles {
  type: 'addFiles';
  payload: {
    files: File[];
    mediaGroupId?: string;
    indexInExistingList?: number;
    listId?: string;
    onFileUploaded?: QueueState['onFileUploaded'];
    addNotificationAction: NotificationStoreActionsType['addTaskToQueue'];
    cancelUpload: UploaderFunctions['cancelUpload'];
  };
  containerId: string;
}

export interface ActionSetProgress {
  type: 'setProgress';
  payload: {
    id: string;
    percentageDone: number;
    speed: number;
    timeRemaining: number;
    numericId?: number;
    updateNotificationAction: NotificationStoreActionsType['updateTaskInQueue'];
  };
}

export interface ActionSetFileDone {
  type: 'setFileDone';
  payload: {
    id: string;
    attributes: {
      mediaNumericId?: number;
      statusMessage: string;
      altStatusMessage: string;
    };
    completeNotificationAction: NotificationStoreActionsType['setTaskComplete'];
  };
}

export interface ActionSetError {
  type: 'setError';
  payload: {
    id: string;
    error: Error;
    failNotificationAction: NotificationStoreActionsType['setTaskFailed'];
  };
}

export interface ActionRemoveFile {
  type: 'removeFile';
  payload: {
    id: string;
    removeNotificationAction: NotificationStoreActionsType['removeTaskFromQueue'];
  };
}

export interface ActionSetIsUploadStarted {
  type: 'isUploadStarted';
  payload: {
    isUploadStarted: boolean;
  };
}

export type QueueReducerAction =
  | ActionAddFiles
  | ActionRemoveFile
  | ActionSetError
  | ActionSetFileDone
  | ActionSetIsUploadStarted
  | ActionSetProgress;

export const types = {
  addFiles: 'addFiles' as const,
  setProgress: 'setProgress' as const,
  setFileDone: 'setFileDone' as const,
  setError: 'setError' as const,
  removeFile: 'removeFile' as const,
  setIsUploadStarted: 'isUploadStarted' as const,
};

export const formatTime = (secs: number): string => {
  if (secs === Number.POSITIVE_INFINITY) {
    return '';
  }
  if (secs < secondsInMinute) {
    return `${Math.round(secs)}s left`;
  }
  if (secs < secondsInHour) {
    const minsRemaining = Math.round(secs / secondsInMinute);
    return `${minsRemaining} min left`;
  }
  if (secs < secondsInHour * hoursInDay) {
    const hoursRemaining = Math.round(secs / secondsInHour);
    return `${hoursRemaining} hour${hoursRemaining > 1 ? 's' : ''} left`;
  }
  const daysRemaining = Math.round(secs / (secondsInHour * hoursInDay));
  return `${daysRemaining} day${daysRemaining > 1 ? 's' : ''} left`;
};

export const formatSpeed = (speed: number): string =>
  `${((speed / million) * byte).toFixed(1)} Mb/s`;

const getStatusMessage = ({ percentageDone, timeRemainingFormatted }) =>
  percentageDone === 1
    ? 'Upload finished! Creating media...'
    : `Uploading ${Math.round(
        percentageDone * 100,
      )}% ${timeRemainingFormatted} - soon you'll be able to customize and share!`;

const getAltStatusMessage = ({ percentageDone, speedFormatted }) =>
  percentageDone === 1
    ? 'Almost there!'
    : `Uploading ${Math.round(percentageDone * 100)}% - ${speedFormatted}`;

const extractName = (file: File) => {
  const name = file.name.split('.');
  name.pop();
  return name.join('.');
};

// takes a list of file objects and adds state for each one.
const addFiles = (_state: File[], action: ActionAddFiles): FileState[] =>
  action.payload.files.map((file: File): FileState => {
    const fileState: FileState = {
      file,
      name: extractName(file),
      isQueued: true,
      isDone: false,
      progress: 0,
      timeRemaining: 0,
      speed: 0,
      id: v4(),
      mediaGroupId: action.payload.mediaGroupId,
      positionInList: {
        indexInExistingList: action.payload.indexInExistingList,
        listId: action.payload.listId,
      },
      onFileUploaded: action.payload.onFileUploaded,
    };

    action.payload.addNotificationAction({
      id: fileState.id,
      type: 'UPLOAD',
      data: fileState,
      onCancelTask: () => {
        action.payload.cancelUpload(action.containerId, fileState.id);
      },
      TTL_MS: secondsInHour * msInSec,
    });

    return fileState;
  });

const changeOnId = (
  state: FileState[],
  targetId: string,
  changes: object,
  updateNotificationAction?: NotificationStoreActionsType['updateTaskInQueue'],
) =>
  state.map((entry) => {
    if (entry.id === targetId) {
      const updatedEntry = {
        ...entry,
        ...changes,
      };
      updateNotificationAction?.(entry.id, updatedEntry);
      return updatedEntry;
    }
    return entry;
  });

// update the progress of an individual entry
const setProgress = (
  state: FileState[],
  {
    payload: { id, percentageDone, speed, timeRemaining, numericId, updateNotificationAction },
  }: {
    payload: {
      id: string;
      percentageDone: number;
      speed: number;
      timeRemaining: number;
      numericId?: number;
      updateNotificationAction: NotificationStoreActionsType['updateTaskInQueue'];
    };
  },
) => {
  const timeRemainingFormatted = formatTime(timeRemaining);
  const speedFormatted = formatSpeed(speed);
  return changeOnId(
    state,
    id,
    {
      progress: percentageDone,
      speed,
      timeRemaining,
      timeRemainingFormatted,
      speedFormatted,
      mediaNumericId: numericId,
      statusMessage: getStatusMessage({ percentageDone, timeRemainingFormatted }),
      altStatusMessage: getAltStatusMessage({ percentageDone, speedFormatted }),
    },
    updateNotificationAction,
  );
};

const getNextId = (state: FileState[], id: string) => {
  const targetEntry: FileState | undefined = state.find((entry) => entry.id === id);

  if (isNil(targetEntry)) {
    return '';
  }

  const targetIndex: number = state.indexOf(targetEntry);
  const nextEntry: FileState | undefined = state[targetIndex + 1];

  if (isNil(nextEntry)) {
    return '';
  }

  return nextEntry.id;
};

// set a specific file to a `done` state, then dequeue the next in the list
// if it exists. dequeuing will tell the UploadInstance to start uploading it
const setFileDone = (
  state: FileState[],
  {
    payload,
  }: {
    payload: {
      id: string;
      attributes: {
        mediaNumericId?: number;
        statusMessage: string;
        altStatusMessage: string;
      };
      completeNotificationAction: NotificationStoreActionsType['setTaskComplete'];
    };
  },
) => {
  const nextState = changeOnId(
    state,
    payload.id,
    {
      ...payload.attributes,
      isDone: true,
      isQueued: false,
    },
    payload.completeNotificationAction,
  );

  const nextId = getNextId(state, payload.id);

  return changeOnId(nextState, nextId, {
    isQueued: false,
  });
};

// set the error property on an entry
const setError = (
  state: FileState[],
  {
    payload,
  }: {
    payload: {
      id: string;
      error: Error;
      failNotificationAction: NotificationStoreActionsType['setTaskFailed'];
    };
  },
) =>
  changeOnId(
    state,
    payload.id,
    {
      error: payload.error,
    },
    payload.failNotificationAction,
  );

const dequeueFirstUpload = (state: FileState[]) => {
  const firstEntry: FileState | undefined = state[0];

  if (isNil(firstEntry)) {
    return state;
  }

  return changeOnId(state, firstEntry.id, {
    isQueued: false,
  });
};

const removeFile = (
  state: FileState[],
  {
    payload,
  }: {
    payload: {
      id: string;
      removeNotificationAction: NotificationStoreActionsType['removeTaskFromQueue'];
    };
  },
) => {
  const entryToRemove: FileState | undefined = state.find((entry) => entry.id === payload.id);
  if (isNil(entryToRemove)) {
    return state;
  }

  let nextState = state;

  if (!entryToRemove.isQueued) {
    const nextId = getNextId(state, payload.id);
    nextState = changeOnId(state, nextId, {
      isQueued: false,
    });
  }

  payload.removeNotificationAction(payload.id);
  return nextState.filter((entry) => entry.id !== payload.id);
};

export const queueReducer = (
  state: File[] | FileState[] | undefined,
  action: QueueReducerAction,
): FileState[] => {
  switch (action.type) {
    case types.addFiles:
      return addFiles(state as File[], action);
    case types.setProgress:
      return setProgress(state as FileState[], action);
    case types.setFileDone:
      return setFileDone(state as FileState[], action);
    case types.setError:
      return setError(state as FileState[], action);
    case types.removeFile:
      return removeFile(state as FileState[], action);
    case types.setIsUploadStarted:
      return dequeueFirstUpload(state as FileState[]);
    default:
      throw new Error('unhandled action');
  }
};
